import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    ListItemIcon,
    Typography,
    Box,
    Breadcrumbs,
    Link
} from '@mui/material';
import {
    Folder as FolderIcon,
    FolderOpen as FolderOpenIcon,
    Home as HomeIcon
} from '@mui/icons-material';
import { useDataProvider, useNotify } from 'react-admin';

interface Folder {
    id: number;
    name: string;
    parent?: number | string | { id: number | string } | null;
}

const parentIdOf = (folder: Folder): number | undefined => {
    const parent = typeof folder.parent === 'object' ? folder.parent?.id : folder.parent;
    return parent ? Number(parent) : undefined;
};

interface FileMoveDialogProps {
    open: boolean;
    file: any | null;
    onClose: () => void;
    onMove: () => void;
}

const FileMoveDialog: React.FC<FileMoveDialogProps> = ({ open, file, onClose, onMove }) => {
    const [folders, setFolders] = useState<Folder[]>([]);
    const [currentFolderId, setCurrentFolderId] = useState<number | undefined>();
    const [selectedFolderId, setSelectedFolderId] = useState<number | undefined>();
    const [folderPath, setFolderPath] = useState<Folder[]>([]);
    const dataProvider = useDataProvider();
    const notify = useNotify();

    useEffect(() => {
        if (open) {
            loadFolders();
            if (file?.folder) {
                setCurrentFolderId(Number(file.folder.id ?? file.folder));
            } else {
                setCurrentFolderId(undefined);
            }
        }
    }, [open, file]);

    const loadFolders = async () => {
        try {
            const { data } = await dataProvider.getList('media_folders', {
                pagination: { page: 1, perPage: 1000 },
                sort: { field: 'name', order: 'ASC' },
                filter: {}
            });
            setFolders(data);
        } catch (error) {
            notify('Fehler beim Laden der Ordner', { type: 'error' });
        }
    };

    const buildFolderPath = (folderId: number | undefined): Folder[] => {
        if (!folderId) return [];
        const path: Folder[] = [];
        let current = folders.find(f => f.id === folderId);

        while (current) {
            path.unshift(current);
            const parentId = parentIdOf(current);
            if (parentId) {
                current = folders.find(f => f.id === parentId);
            } else {
                break;
            }
        }
        return path;
    };

    const getCurrentFolderChildren = (): Folder[] => {
        return folders.filter(f => {
            if (selectedFolderId === undefined) {
                return !parentIdOf(f);
            }
            return parentIdOf(f) === selectedFolderId;
        });
    };

    const handleFolderClick = (folderId: number) => {
        setSelectedFolderId(folderId);
    };

    const handleBreadcrumbClick = (folderId: number | undefined) => {
        setSelectedFolderId(folderId);
    };

    const handleMove = async () => {
        if (!file) return;

        try {
            const folderIRI = selectedFolderId ? `/api/media_folders/${selectedFolderId}` : null;

            await dataProvider.update('media_files', {
                id: file.id,
                data: {
                    folder: folderIRI
                },
                previousData: file
            });

            notify('Datei erfolgreich verschoben', { type: 'success' });
            onMove();
            onClose();
        } catch (error) {
            notify('Fehler beim Verschieben', { type: 'error' });
        }
    };

    useEffect(() => {
        if (folders.length > 0) {
            setFolderPath(buildFolderPath(selectedFolderId));
        }
    }, [selectedFolderId, folders]);

    if (!file) return null;

    const children = getCurrentFolderChildren();
    const isCurrentFolder = selectedFolderId === currentFolderId;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Datei verschieben</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Datei: <strong>{file.originalFilename}</strong>
                </Typography>

                <Breadcrumbs sx={{ mb: 2 }}>
                    <Link
                        component="button"
                        variant="body2"
                        onClick={() => handleBreadcrumbClick(undefined)}
                        sx={{ display: 'flex', alignItems: 'center' }}
                    >
                        <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
                        Alle Dateien
                    </Link>
                    {folderPath.map((folder) => (
                        <Link
                            key={folder.id}
                            component="button"
                            variant="body2"
                            onClick={() => handleBreadcrumbClick(folder.id)}
                        >
                            {folder.name}
                        </Link>
                    ))}
                </Breadcrumbs>

                <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, minHeight: 300, maxHeight: 400, overflow: 'auto' }}>
                    {children.length === 0 ? (
                        <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                            <Typography variant="body2">
                                Keine Unterordner vorhanden
                            </Typography>
                        </Box>
                    ) : (
                        <List>
                            {children.map((folder) => (
                                <ListItem key={folder.id} disablePadding>
                                    <ListItemButton onClick={() => handleFolderClick(folder.id)}>
                                        <ListItemIcon>
                                            <FolderIcon />
                                        </ListItemIcon>
                                        <ListItemText primary={folder.name} />
                                    </ListItemButton>
                                </ListItem>
                            ))}
                        </List>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Abbrechen</Button>
                <Button
                    onClick={handleMove}
                    variant="contained"
                    disabled={isCurrentFolder}
                >
                    {isCurrentFolder ? 'Bereits in diesem Ordner' : 'Hierher verschieben'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default FileMoveDialog;
